import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Fab, Grid, Stack, Tooltip, Typography } from '@mui/material';
import { off, onValue, ref } from 'firebase/database';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dateDisplay } from '../Helpers/dateDisplay';
import { SessionWithId } from '../Models/session';
import { theme } from '../Mui/Theme';
import { RootState } from '../Store';
import { openAlert } from '../Store/AlertSlice';
import { deselectSession } from '../Store/SessionsSlice';
import { DeleteSession } from '../firebase/Database/DeleteSession';
import { getSession } from '../firebase/Database/GetSession';
import { database } from '../firebase/firebase';
import { AddEditSessionDialog } from './AddEditSessionDialog';
import { AddPersonToSessionDialog } from './AddPersonToSessionDialog';
import { BoxPerson } from './BoxPerson';
import { BoxBodyText } from './Text/BoxBodyText';
import { BoxSubtitle } from './Text/BoxSubtitle';
import { BoxTitle } from './Text/BoxTitle';

export const SessionDetailsCard: React.FC = () => {
    const selectedSession = useSelector(
        (state: RootState) => state.sessionsSlice.selectedSession,
    );

    const [removePeople, setRemovePeople] = useState(false);

    const dispatch = useDispatch();

    const [currentSession, setCurrentSession] = useState<SessionWithId | null>(
        null,
    );

    const [showDialog, setShowDialog] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [openEditSessionDialog, setOpenEditSessionDialog] = useState(false);

    useEffect(() => {
        if (currentSession) {
            const sessionRef = ref(database, `sessions/${currentSession.id}`);
            onValue(sessionRef, (snapshot) => {
                const sessionData = snapshot.val();
                if (sessionData) {
                    // Check if the 'people' object has changed
                    if (
                        JSON.stringify(sessionData.people) !==
                        JSON.stringify(currentSession.session.people)
                    ) {
                        setRefresh(!refresh);
                    }
                }
            });

            return () => {
                off(sessionRef);
            };
        }
    }, [refresh, currentSession]);

    const closeEditSessionDialog = () => {
        setOpenEditSessionDialog(false);
    };

    const openEditSessionDialogHandler = () => {
        setOpenEditSessionDialog(true);
    };

    useEffect(() => {
        if (selectedSession) {
            getSession(selectedSession)
                .then((res) => {
                    setCurrentSession(res);
                })
                .catch((err) => {
                    dispatch(
                        openAlert({
                            displayText:
                                'Nemohu načíst aktuální lekci. Zkuste to později, případně kontaktujte správce',
                            severity: 'error',
                        }),
                    );
                });
        }
    }, [selectedSession, dispatch, refresh]);

    const peopleComponents =
        currentSession &&
        currentSession.session.people &&
        Object.keys(currentSession.session.people).map((personId) => {
            return (
                <BoxPerson
                    key={personId}
                    personId={personId}
                    remove={removePeople}
                    sessionId={currentSession.id}
                />
            );
        });

    const addPersonHandler = () => {
        if (currentSession) {
            setShowDialog(true);
        }
    };

    const deleteSession = () => {
        if (!selectedSession) {
            dispatch(
                openAlert({
                    displayText: 'Není vybrána lekce',
                    severity: 'error',
                }),
            );
            return;
        }

        DeleteSession(selectedSession)
            .then(() => {
                dispatch(
                    openAlert({
                        displayText: 'Lekce byla úspěšně zrušena',
                        severity: 'success',
                    }),
                );
                dispatch(deselectSession());
                setCurrentSession(null);
            })
            .catch((err) =>
                dispatch(
                    openAlert({
                        displayText: `Problém při rušení lekce, ${err.message}`,
                        severity: 'error',
                    }),
                ),
            );
    };

    return (
        <Box
            sx={{
                background: theme.palette.primary.dark,
                marginTop: '50px',
                marginBottom: '50px',
                borderRadius: '10px',
                height: '75vh',
                position: 'relative',
                minWidth: '100%',
                overflowY: 'auto',
            }}
        >
            {currentSession && (
                <>
                    <AddPersonToSessionDialog
                        open={showDialog}
                        close={() => setShowDialog(false)}
                        sessionId={currentSession.id}
                    />
                    <AddEditSessionDialog
                        open={openEditSessionDialog}
                        close={closeEditSessionDialog}
                        session={currentSession}
                        edit={true}
                    />
                </>
            )}
            {!currentSession && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: '15px',
                    }}
                >
                    <Typography variant="h5" style={{ fontWeight: 'bold' }}>
                        Nemáte vybranou lekci.
                    </Typography>
                </Box>
            )}
            {currentSession && (
                <>
                    <Grid
                        container
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            justifyContent: 'flex-end',
                            margin: '5px',
                        }}
                        spacing={1}
                    >
                        <Grid item>
                            <Tooltip title="Smazat lekci">
                                <Fab
                                    color="secondary"
                                    onClick={deleteSession}
                                    sx={{
                                        color: theme.palette.primary.main,
                                    }}
                                    size="small"
                                >
                                    <DeleteIcon />
                                </Fab>
                            </Tooltip>
                        </Grid>
                        <Grid item>
                            <Tooltip title="Upravit lekci">
                                <Fab
                                    color="secondary"
                                    onClick={openEditSessionDialogHandler}
                                    sx={{
                                        color: theme.palette.primary.main,
                                    }}
                                    size="small"
                                >
                                    <EditIcon />
                                </Fab>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <Stack sx={{ maxHeight: '100%' }}>
                        <BoxTitle>Detaily lekce</BoxTitle>
                        <BoxSubtitle>Datum: </BoxSubtitle>
                        <BoxBodyText>
                            {dateDisplay(currentSession.session)}
                        </BoxBodyText>
                        <BoxSubtitle>Kapacita: </BoxSubtitle>
                        <BoxBodyText>
                            {currentSession.session.people
                                ? Object.keys(currentSession.session.people)
                                      .length
                                : '0'}
                            /{currentSession.session.capacity}
                        </BoxBodyText>
                        <BoxSubtitle>Popis: </BoxSubtitle>
                        <BoxBodyText>
                            {currentSession.session.description}
                        </BoxBodyText>
                        <BoxSubtitle>Adresa: </BoxSubtitle>
                        <BoxBodyText>
                            {currentSession.session.location}
                        </BoxBodyText>
                        <Grid
                            container
                            display="flex"
                            direction="row"
                            justifyContent="space-between"
                        >
                            <Grid item>
                                <BoxSubtitle>Lidé: </BoxSubtitle>
                            </Grid>
                            <Grid item marginRight="15px" marginTop="5px">
                                <Tooltip title="Odhlásit někoho z lekce">
                                    <Fab
                                        color="secondary"
                                        sx={{
                                            margin: '0 5px',
                                            color: theme.palette.primary.main,
                                            background: removePeople
                                                ? `${theme.palette.secondary.dark}`
                                                : 'secondary',
                                        }}
                                        size="small"
                                        onClick={() => {
                                            setRemovePeople(!removePeople);
                                        }}
                                    >
                                        <RemoveIcon />
                                    </Fab>
                                </Tooltip>
                                <Tooltip title="Přihlásit někoho na lekci">
                                    <Fab
                                        color="secondary"
                                        sx={{
                                            margin: '0 5px',
                                            color: theme.palette.primary.main,
                                        }}
                                        size="small"
                                        onClick={addPersonHandler}
                                    >
                                        <AddIcon />
                                    </Fab>
                                </Tooltip>
                            </Grid>
                        </Grid>
                        <Box
                            sx={{
                                height: 'auto',
                                width: '100%',
                                overflowY: 'auto',
                                minHeight: '200px',
                            }}
                        >
                            {peopleComponents}
                        </Box>
                    </Stack>
                </>
            )}
        </Box>
    );
};
